import {
    Alert,
    Box,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { useDeveloperApi } from './api';
import { DeveloperShell, ErrorAlert, Loading, TableScroll } from './components';
import QuotaPanel from './QuotaPanel';
import type { DeveloperMetrics } from './types';

// Response classes are identities, not states: the first four categorical slots in their fixed order
// (dataviz reference palette, validated: adjacent CVD ΔE >= 9.1). Two slots sit below 3:1 on the
// surface, so the per-day table below is the chart's relief (and its accessible view).
const CLASSES = [
    { key: '2xx', color: '#2a78d6' },
    { key: '3xx', color: '#eb6834' },
    { key: '4xx', color: '#1baf7a' },
    { key: '5xx', color: '#eda100' },
] as const;
const SURFACE = '#ffffff';
const GRID = '#ececec';
const INK_SECONDARY = '#52514e';

const pct = (ratio: number) => `${(ratio * 100).toFixed(ratio > 0 && ratio < 0.01 ? 2 : 1)}%`;

const Tile = ({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) => (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 500, lineHeight: 1.2 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
);

/** Developer metrics (SBX D9): requests per day by status, error rate, p95 latency, bookings, and the
 *  sandbox's quota usage. Days are UTC. From ClickHouse (DSEC D4) there is no history before collection started:
 *  nothing recorded yet is an empty state, and the days before `collected_since` read "no data", not zero. */
const MetricsPage = () => {
    const api = useDeveloperApi();
    const [days, setDays] = React.useState(7);
    const [metrics, setMetrics] = React.useState<DeveloperMetrics | null>(null);
    const [error, setError] = React.useState<unknown>(null);

    const load = React.useCallback(() => {
        setError(null);
        api.metrics(days).then(setMetrics).catch(setError);
    }, [api, days]);
    React.useEffect(() => {
        load();
    }, [load]);

    const fromClickhouse = metrics?.source === 'clickhouse';
    const collectedSince = metrics?.collected_since ?? null;
    // ISO dates compare as strings.
    const noData = (date: string) => fromClickhouse && (collectedSince === null || date < collectedSince);
    const chartData = (metrics?.days || []).filter((d) => !noData(d.date)).map((d) => ({
        day: moment(d.date).format('D MMM'),
        ...d.requests.by_class,
    }));
    const totals = metrics?.totals;
    const empty = fromClickhouse && collectedSince === null;
    const partial = fromClickhouse && collectedSince !== null && metrics !== null && collectedSince > metrics.window.since;

    return (
        <DeveloperShell
            title="Metrics"
            actions={(
                <TextField select size="small" label="Period" value={days} onChange={(ev) => setDays(Number(ev.target.value))} sx={{ minWidth: 150 }}>
                    {[1, 7, 14].map((n) => <MenuItem key={n} value={n}>{n === 1 ? 'Today' : `Last ${n} days`}</MenuItem>)}
                </TextField>
            )}
        >
            {error ? <ErrorAlert error={error} onRetry={load} /> : null}
            {!metrics && !error ? <Loading /> : null}
            {metrics && empty && (
                <Alert severity="info" sx={{ my: 1.5 }} data-testid="metrics-empty">
                    No metrics recorded yet. Requests and bookings are counted from the day collection was switched on;
                    there is no earlier history. They appear here within a minute of the first request.
                </Alert>
            )}
            {metrics && totals && !empty && (
                <Box data-testid="metrics">
                    {partial && (
                        <Alert severity="info" sx={{ mt: 1.5 }} data-testid="metrics-partial">
                            {`Collected since ${collectedSince} (UTC): earlier days have no data.`}
                        </Alert>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, my: 1.5 }}>
                        <Tile label="Requests" value={totals.requests.total.toLocaleString()} sub={`${metrics.window.since} – ${metrics.window.until} (UTC)`} />
                        <Tile label="Error rate" value={pct(totals.requests.error_rate)} sub="5xx and failed deliveries" />
                        <Tile
                            label="p95 latency"
                            value={totals.requests.p95_latency_ms != null ? `${totals.requests.p95_latency_ms} ms` : '—'}
                            sub={fromClickhouse ? 'approximate' : undefined}
                        />
                        <Tile
                            label="Bookings"
                            value={(totals.bookings.created + totals.bookings.updated).toLocaleString()}
                            sub={`${totals.bookings.created} created · ${totals.bookings.updated} updated · ${totals.bookings.cancelled} cancelled`}
                        />
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Requests per day by status</Typography>
                        <Box sx={{ width: '100%', height: 240 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                    <CartesianGrid vertical={false} stroke={GRID} />
                                    <XAxis dataKey="day" tick={{ fill: INK_SECONDARY, fontSize: 12 }} axisLine={{ stroke: GRID }} tickLine={false} />
                                    <YAxis allowDecimals={false} tick={{ fill: INK_SECONDARY, fontSize: 12 }} axisLine={false} tickLine={false} width={44} />
                                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                                    <Legend wrapperStyle={{ fontSize: 12, color: INK_SECONDARY }} />
                                    {CLASSES.map((c, idx) => (
                                        <Bar
                                            key={c.key}
                                            dataKey={c.key}
                                            name={c.key}
                                            stackId="requests"
                                            fill={c.color}
                                            stroke={SURFACE}
                                            strokeWidth={2}
                                            maxBarSize={24}
                                            radius={idx === CLASSES.length - 1 ? [4, 4, 0, 0] : undefined}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                    <TableScroll>
                        <Table size="small" sx={{ mt: 2 }} data-testid="metrics-days">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Day (UTC)</TableCell>
                                    <TableCell align="right">Requests</TableCell>
                                    {CLASSES.map((c) => <TableCell key={c.key} align="right">{c.key}</TableCell>)}
                                    <TableCell align="right">Error rate</TableCell>
                                    <TableCell align="right">p95</TableCell>
                                    <TableCell align="right">Bookings created / updated</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {metrics.days.map((d) => (noData(d.date) ? (
                                    <TableRow key={d.date} data-testid="metrics-no-data">
                                        <TableCell>{d.date}</TableCell>
                                        <TableCell align="right" colSpan={CLASSES.length + 4} sx={{ color: 'text.secondary' }}>No data</TableCell>
                                    </TableRow>
                                ) : (
                                    <TableRow key={d.date}>
                                        <TableCell>{d.date}</TableCell>
                                        <TableCell align="right">{d.requests.total}</TableCell>
                                        {CLASSES.map((c) => <TableCell key={c.key} align="right">{d.requests.by_class[c.key]}</TableCell>)}
                                        <TableCell align="right">{pct(d.requests.error_rate)}</TableCell>
                                        <TableCell align="right">{d.requests.p95_latency_ms != null ? `${d.requests.p95_latency_ms} ms` : '—'}</TableCell>
                                        <TableCell align="right">{d.bookings.created} / {d.bookings.updated}</TableCell>
                                    </TableRow>
                                )))}
                            </TableBody>
                        </Table>
                    </TableScroll>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }} data-testid="metrics-bookings-help">
                        Bookings per UTC day: created = new bookings; updated = distinct bookings changed that day, however many
                        times; cancelled = bookings that became cancelled.
                    </Typography>
                </Box>
            )}
            {metrics?.quotas && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2, maxWidth: 560 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Quota usage</Typography>
                    <QuotaPanel quotas={metrics.quotas} />
                </Paper>
            )}
        </DeveloperShell>
    );
};

export default MetricsPage;
